import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';

import { AnswerStatus, GetQuizzesResponse, Quiz, QuizStoreState, StatData, Student, StudentsMap } from './typings';

const initialState: QuizStoreState = {
    quizzes: [] as Quiz[],
    quizzesLoadingStatus: LOADING_STATUSES.UNSENT,
    quizzesToDelete: {},
    studentsMap: {} as StudentsMap,
    statData: null,
};

const quizSlice = createSlice({
    name: 'QUIZ',
    initialState: initialState,
    reducers: {
        getQuizzes: (state: QuizStoreState) => {
            state.quizzesLoadingStatus = LOADING_STATUSES.LOADING;
        },

        setQuizzes: (state: QuizStoreState, action: PayloadAction<GetQuizzesResponse>) => {
            state.quizzes = [...action.payload.results];

            state.quizzesLoadingStatus = LOADING_STATUSES.SUCCESS;
        },

        deleteQuiz: (state: QuizStoreState, action: PayloadAction<string>) => {
            const quizId = action.payload;

            const quizToDeleteIndex = state.quizzes.findIndex((quiz) => quiz.id === quizId);

            if (quizToDeleteIndex !== undefined) {
                state.quizzesToDelete[quizId] = {
                    index: quizToDeleteIndex,
                    quiz: state.quizzes[quizToDeleteIndex],
                };

                state.quizzes = state.quizzes.filter((quiz) => quiz.id !== quizId);
            }
        },

        restoreQuizFromQuizzesToDelete: (state: QuizStoreState, action: PayloadAction<string>) => {
            const quizId = action.payload;

            if (state.quizzesToDelete[quizId]) {
                const { index, quiz } = state.quizzesToDelete[quizId];

                state.quizzes.splice(index, 0, quiz);

                delete state.quizzesToDelete[quizId];
            }
        },

        deleteQuizFromQuizzesToDelete: (state: QuizStoreState, action: PayloadAction<string>) => {
            const quizId = action.payload;

            if (state.quizzesToDelete[quizId]) {
                delete state.quizzesToDelete[quizId];
            }
        },

        setQuizToPreview: (state: QuizStoreState, action: PayloadAction<Quiz>) => {
            state.quizToPreview = action.payload;
        },

        setRoomPin: (state: QuizStoreState, action: PayloadAction<string>) => {
            state.roomPin = action.payload;
        },

        setStudentOnlineStatus: (state: QuizStoreState, action: PayloadAction<{ id: string; isOnline: boolean }>) => {
            state.studentsMap[action.payload.id].isOnline = action.payload.isOnline;
        },

        addStudent: (state: QuizStoreState, action: PayloadAction<{ id: string; name: string }>) => {
            const studentParams: Student = {
                name: action.payload.name,
                isOnline: true,
                answers: {},
                id: action.payload.id,
            };

            state.studentsMap[action.payload.id] = studentParams;
        },

        setStudentAnswerStatus: (
            state: QuizStoreState,
            action: PayloadAction<{ id: string; questionIndex: number; answerStatus: AnswerStatus }>
        ) => {
            state.studentsMap[action.payload.id].answers[action.payload.questionIndex] = action.payload.answerStatus;
        },

        resetAllStudentsAnswersStatus: (state: QuizStoreState) => {
            const newMap = { ...state.studentsMap };
            for (const student of Object.values(newMap)) {
                student.answers = {};
            }
            state.studentsMap = newMap;
        },

        setStatData: (state: QuizStoreState, action: PayloadAction<StatData>) => {
            state.statData = action.payload;
        },
    },
});

export const quizActions = quizSlice.actions;
export const quizReducer = quizSlice.reducer;
